import type { Board } from "../board";
import { ALL_DIRECTIONS, directionDelta, isDiagonal } from "../direction";
import { XY } from "../xy";
import { MinHeap } from "./minHeap";

/**
 * Cost to enter `to` from `from`. Return `null` for impassable; otherwise >= 1.
 *
 * Don't pre-apply a diagonal multiplier — `findPath` does that itself, so callers
 * write costs in plain terrain units (grass=1, concrete=4, fire=50).
 */
export type StepCost = (from: XY, to: XY) => number | null;

export interface PathfindOpts {
	/** Hard cap on nodes expanded. Defaults to width*height (the natural A* bound). */
	maxNodes?: number;
}

/** Diagonal moves cost this much more than cardinal. Pure euclidean would be ~1.414. */
const DIAGONAL_COST_MULTIPLIER = 1.6;

const NO_PARENT = -1;

interface Neighbor {
	delta: XY;
	isDiagonal: boolean;
}

const NEIGHBORS: readonly Neighbor[] = ALL_DIRECTIONS.map((d) => ({
	delta: directionDelta(d),
	isDiagonal: isDiagonal(d),
}));

/** Admissible A* heuristic when every step cost is >= 1. */
function octileDistance(a: XY, b: XY): number {
	const dx = Math.abs(a.x - b.x);
	const dy = Math.abs(a.y - b.y);
	const min = Math.min(dx, dy);
	const max = Math.max(dx, dy);
	return max - min + min * DIAGONAL_COST_MULTIPLIER;
}

/**
 * A* pathfind from `start` to `target`.
 *
 * Returns the steps to walk (excluding `start`, including `target`),
 * `[]` when `start` equals `target`, or `null` when no path exists.
 */
export function findPath(
	board: Board,
	start: XY,
	target: XY,
	stepCost: StepCost,
	opts: PathfindOpts = {},
): XY[] | null {
	if (!board.isInBounds(start) || !board.isInBounds(target)) return null;
	if (start.equals(target)) return [];

	const { width } = board;
	const cellCount = width * board.height;
	const encode = (x: number, y: number) => y * width + x;
	const decode = (idx: number) => new XY(idx % width, Math.floor(idx / width));

	const startIdx = encode(start.x, start.y);
	const targetIdx = encode(target.x, target.y);

	// A* terms used below: g = known cost from start to a cell; h = heuristic estimate
	// of remaining cost to target (octileDistance); f = g + h, the priority the open
	// heap sorts by — we always expand the cell with the lowest expected total cost.
	const gScore = new Float64Array(cellCount).fill(Infinity);
	gScore[startIdx] = 0;
	const cameFrom = new Int32Array(cellCount).fill(NO_PARENT);
	const closed = new Uint8Array(cellCount);

	const open = new MinHeap<{ f: number; idx: number }>((a, b) => a.f - b.f);
	open.push({ f: octileDistance(start, target), idx: startIdx });

	const maxNodes = opts.maxNodes ?? cellCount;
	let nodesExpanded = 0;

	const tryReachNeighbor = (fromIdx: number, fromXY: XY, neighbor: Neighbor): void => {
		const toXY = fromXY.add(neighbor.delta);
		if (!toXY.isInside(board)) return;
		const toIdx = encode(toXY.x, toXY.y);
		if (closed[toIdx]) return;

		const baseCost = stepCost(fromXY, toXY);
		if (baseCost === null) return;

		const moveCost = neighbor.isDiagonal ? baseCost * DIAGONAL_COST_MULTIPLIER : baseCost;
		const tentativeG = gScore[fromIdx] + moveCost;
		if (tentativeG >= gScore[toIdx]) return;

		gScore[toIdx] = tentativeG;
		cameFrom[toIdx] = fromIdx;
		open.push({ f: tentativeG + octileDistance(toXY, target), idx: toIdx });
	};

	const reconstructPath = (): XY[] => {
		const path: XY[] = [];
		for (let idx = targetIdx; idx !== startIdx; idx = cameFrom[idx]) path.push(decode(idx));
		path.reverse();
		return path;
	};

	while (open.size > 0) {
		const { idx: currentIdx } = open.pop()!;
		if (closed[currentIdx]) continue;
		closed[currentIdx] = 1;

		if (++nodesExpanded > maxNodes) return null;
		if (currentIdx === targetIdx) return reconstructPath();

		const currentXY = decode(currentIdx);
		for (const neighbor of NEIGHBORS) tryReachNeighbor(currentIdx, currentXY, neighbor);
	}

	return null;
}
